/**
 * Booking visual palette + small primitives.
 *
 * The CSS in this project uses an inverted naming convention: the JS flag
 * `useTheme().isDarkMode` is the *opposite* of the visually-dark mode. To
 * keep the rest of the booking code easy to read we always work with
 * `isVisuallyDark = !isDarkMode` and pick palette tokens from here.
 */

export function getBookingPalette(isVisuallyDark) {
  if (isVisuallyDark) {
    return {
      pageBg: "transparent",
      surface: "hsl(234 30% 18%)",
      surfaceAlt: "hsl(234 28% 22%)",
      surfaceHover: "hsl(234 30% 24%)",
      border: "1px solid hsl(234 20% 28%)",
      borderStrong: "1px solid hsl(234 20% 36%)",
      text: "hsl(0 0% 96%)",
      textMuted: "hsl(0 0% 75%)",
      textSubtle: "hsl(0 0% 60%)",
      accent: "hsl(166 80% 55%)",
      accentSoft: "hsla(166, 80%, 55%, 0.16)",
      accentBorder: "hsl(166 75% 50%)",
      shadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
      shadowSoft: "0 2px 10px rgba(0, 0, 0, 0.25)",
      progressTrack: "hsl(234 20% 28%)",
      progressBar:
        "linear-gradient(90deg, hsl(166 80% 55%), hsl(184 75% 55%))",
      stepDoneBg: "hsl(166 70% 45%)",
      stepActiveBg: "hsl(166 80% 55%)",
      stepIdleBg: "hsl(234 25% 26%)",
      stepIdleText: "hsl(0 0% 65%)",
      stepActiveText: "#0b1020",
      stepDoneText: "#ffffff",
      stepLabelActive: "hsl(0 0% 96%)",
      stepLabelIdle: "hsl(0 0% 65%)",
    };
  }
  return {
    pageBg: "transparent",
    surface: "hsl(0 0% 100%)",
    surfaceAlt: "hsl(220 30% 98%)",
    surfaceHover: "hsl(220 25% 96%)",
    border: "1px solid hsl(220 20% 92%)",
    borderStrong: "1px solid hsl(220 25% 82%)",
    text: "hsl(220 30% 18%)",
    textMuted: "hsl(220 15% 38%)",
    textSubtle: "hsl(220 12% 55%)",
    accent: "hsl(166 100% 32%)",
    accentSoft: "hsla(166, 100%, 41%, 0.10)",
    accentBorder: "hsl(166 100% 38%)",
    shadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    shadowSoft: "0 2px 10px rgba(15, 23, 42, 0.06)",
    progressTrack: "hsl(220 20% 90%)",
    progressBar:
      "linear-gradient(90deg, hsl(166 100% 38%), hsl(184 70% 45%))",
    stepDoneBg: "hsl(166 80% 38%)",
    stepActiveBg: "hsl(166 100% 38%)",
    stepIdleBg: "hsl(220 20% 92%)",
    stepIdleText: "hsl(220 15% 50%)",
    stepActiveText: "#ffffff",
    stepDoneText: "#ffffff",
    stepLabelActive: "hsl(220 30% 18%)",
    stepLabelIdle: "hsl(220 15% 50%)",
  };
}

/** Maps a backend storage path (e.g. "/storage/...") to an absolute URL. */
export function resolveImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_SECOND_API_KEY || "").replace(/\/+$/, "");
  const rel = path.startsWith("/") ? path : `/${path}`;
  return `${base}${rel}`;
}
